import type {
    KontentRichText,
    BrandColor,
    CTALocation,
    KontentMedia,
    CTAConfiguration,
    NormalizedCtaFields,
} from '../../../types/KontentProps';
import { getMediaData, getMultiChoice } from '../Global/DataUtils';
import { plainTextFromHtml } from '../../utils/plainTextFromHtml';

export const AUTO_ADVANCE_MS = 7000;

export const DEFAULT_GRADIENT_START = '#EF6825';
export const DEFAULT_GRADIENT_END = '#B22E34';

export interface NormalizedContentExplorerSlide extends NormalizedCtaFields {
    id: string;
    navLabel: string;
    headline?: KontentRichText;
    subHeadline?: KontentRichText;
    description?: KontentRichText;
    highlightValue?: KontentRichText;
    supportingContext?: KontentRichText;
    contextLabel?: KontentRichText;
    imageUrl: string | null;
    imageAlt: string;
    categories: CategoryItem[];
}

export type CategoryItem = {
    id?: string;
    elements?: {
        content?: KontentRichText;
        paragraph_copy_color?: BrandColor;
    };
};

type RawSlideElements = {
    headline?: KontentRichText;
    sub_headline?: KontentRichText;
    description?: KontentRichText;
    highlight_value?: KontentRichText;
    supporting_context?: KontentRichText;
    context_label?: KontentRichText;
    image?: KontentMedia;
    categories?: { value?: CategoryItem[] | CategoryItem };
    cta_configuration__cta_label?: { value?: string };
    cta_configuration__cta_location?: CTALocation;
    cta_configuration__cta_location_anchor_point?: { value?: string };
    cta_configuration__cta_window_action?: CTAConfiguration['cta_configuration__cta_window_action'];
    cta_type?: {
        value?: Array<{ codename?: string; name?: string }>;
    };
    video_link?: { value?: string };
    play_button_accessibility_label?: { value?: string };
};

export type RawContentExplorerSlide = {
    id?: string;
    elements?: RawSlideElements;
};

export function categoriesFromField(
    categories: { value?: CategoryItem[] | CategoryItem } | undefined
): CategoryItem[] {
    const raw = categories?.value;
    const items: CategoryItem[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return items.filter((c) => !!c.elements?.content?.value);
}

export function normalizeContentExplorerSlides(
    linked:
        | {
              value?: RawContentExplorerSlide[] | RawContentExplorerSlide;
          }
        | undefined
): NormalizedContentExplorerSlide[] {
    const raw = linked?.value;
    const items: RawContentExplorerSlide[] = Array.isArray(raw)
        ? raw
        : raw
        ? [raw]
        : [];
    if (!items.length) return [];

    return items.map((item, index) => {
        const el = item.elements;
        const id = item.id || `content-explorer-slide-${index}`;
        const headlineRte = el?.headline;
        const navLabel =
            plainTextFromHtml(headlineRte?.value) || `Program ${index + 1}`;
        const media = getMediaData(el?.image);
        const imageFirst = el?.image?.value?.[0] as
            | { elements?: { caption?: { value?: string | null } } }
            | undefined;
        const caption = imageFirst?.elements?.caption?.value || '';
        const url = media?.url || null;
        const desc = media?.description;
        const name = media?.name;
        const imageAlt = desc || name || caption || '';

        const ctaWindowAction =
            getMultiChoice(el?.cta_configuration__cta_window_action) ??
            undefined;
        const ctaType = getMultiChoice(el?.cta_type) ?? undefined;
        const videoLink = el?.video_link?.value?.trim();
        const playButtonAccessibilityLabel =
            el?.play_button_accessibility_label?.value;

        return {
            id,
            navLabel,
            headline: headlineRte,
            subHeadline: el?.sub_headline,
            description: el?.description,
            highlightValue: el?.highlight_value,
            supportingContext: el?.supporting_context,
            contextLabel: el?.context_label,
            imageUrl: url,
            imageAlt,
            categories: categoriesFromField(el?.categories),
            ctaLabel: el?.cta_configuration__cta_label?.value,
            ctaLocation: el?.cta_configuration__cta_location,
            ctaAnchorPoint:
                el?.cta_configuration__cta_location_anchor_point?.value,
            ctaType,
            videoLink: videoLink || undefined,
            playButtonAccessibilityLabel:
                playButtonAccessibilityLabel || undefined,
            ctaWindowAction,
        };
    });
}
